import React, { useState } from "react";
import { Col, Container, Row, FormGroup, Input, Label } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Btn, H2, P, Image } from "../AbstractElements";
import { dynamicImage } from "../Utils";
import axios from "axios";
import { baseUrl } from "../Utils/FileUpload";
import { useForm, Controller, FieldError } from "react-hook-form";

type FormData = {
  mobile: string;
  otp: string;
};

export default function DeleteAccount() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Request OTP, 2: Confirm Deletion
  const [countryCode, setCountryCode] = useState("+91");

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const onRequestOtp = async (data: FormData) => {
    setLoading(true);
    try {
      const fullMobile = `${countryCode}${data.mobile}`;
      const response = await axios.post(`${baseUrl}request-deletion-otp`, {
        mobile: fullMobile,
      });
      toast.success(response.data.message);
      setStep(2);
    } catch (error: any) {
      console.error("OTP Request error:", error);
      const errorMessage = error.response?.data?.message || "Failed to send OTP";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onConfirmDeletion = async (data: FormData) => {
    setLoading(true);
    try {
      const fullMobile = `${countryCode}${data.mobile}`;
      const response = await axios.post(`${baseUrl}confirm-deletion`, {
        mobile: fullMobile,
        otp: data.otp,
      });
      toast.success(response.data.message);
      reset();
      setStep(1);
      // Redirect to login after successful deletion
      // setTimeout(() => {
      //   navigate("/login");
      // }, 2000);
    } catch (error: any) {
      console.error("Deletion error:", error);
      const errorMessage = error.response?.data?.message || "Failed to delete account";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="p-0" fluid>
      <Row className="m-0">
        <Col xs={12} className="p-0">
          <div className="login-card login-dark">
            <div>
              <div>
                <Link className="logo" to="#">
                  <Image
                    className="img-fluid for-light m-auto"
                    src={dynamicImage(`logo/Logo.png`)}
                    alt="loginpage"
                  />
                  <Image
                    className="img-fluid for-dark"
                    src={dynamicImage(`logo/Logo.png`)}
                    alt="loginpage"
                  />
                </Link>
              </div>
              <div className="login-main">
                <form
                  className="theme-form"
                  onSubmit={handleSubmit(step === 1 ? onRequestOtp : onConfirmDeletion)}
                >
                  <H2 className="text-center">Delete Account</H2>
                  <P className="text-center">
                    {step === 1
                      ? "Enter your mobile number to request account deletion."
                      : "Enter the OTP sent to your WhatsApp to confirm deletion."}
                  </P>

                  <FormGroup>
                    <Label>Mobile Number</Label>
                    <div className="d-flex">
                      <Input
                        type="select"
                        className="form-select"
                        style={{ maxWidth: "100px", marginRight: "10px" }}
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        disabled={step === 2}
                      >
                        <option value="+91">+91 (IN)</option>
                        <option value="+966">+966 (SA)</option>
                        <option value="+971">+971 (AE)</option>
                      </Input>
                      <div className="flex-grow-1">
                        <Controller
                          name="mobile"
                          control={control}
                          defaultValue=""
                          rules={{
                            required: "Please enter your mobile number",
                            pattern: {
                              value: /^[0-9]+$/,
                              message: "Please enter a valid mobile number",
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              type="text"
                              {...field}
                              placeholder="Mobile Number"
                              invalid={!!errors.mobile}
                              disabled={step === 2}
                            />
                          )}
                        />
                        {errors.mobile && (
                          <div className="text-danger">
                            {(errors.mobile as FieldError).message}
                          </div>
                        )}
                      </div>
                    </div>
                  </FormGroup>

                  {step === 2 && (
                    <FormGroup>
                      <Col>
                        <Label>OTP</Label>
                      </Col>
                      <Controller
                        name="otp"
                        control={control}
                        defaultValue=""
                        rules={{
                          required: "Please enter the OTP",
                          minLength: {
                            value: 5,
                            message: "OTP must be 5 digits",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            type="text"
                            {...field}
                            placeholder="Enter OTP"
                            invalid={!!errors.otp}
                          />
                        )}
                      />
                      {errors.otp && (
                        <div className="text-danger">
                          {(errors.otp as FieldError).message}
                        </div>
                      )}
                    </FormGroup>
                  )}

                  <FormGroup className="mb-0">
                    <div className="text-end mt-3">
                      <Btn
                        color="primary"
                        className="w-100"
                        block
                        type="submit"
                        disabled={loading}
                      >
                        {loading
                          ? "Processing..."
                          : step === 1
                          ? "Request OTP"
                          : "Delete Account"}
                      </Btn>
                    </div>
                  </FormGroup>

                  {step === 2 && (
                    <div className="text-center mt-3">
                      <span
                        style={{ cursor: "pointer", color: "blue" }}
                        onClick={() => setStep(1)}
                      >
                        Change Mobile Number
                      </span>
                    </div>
                  )}

                  <div className="text-center mt-3">
                    <Link className="link" to="/login">
                      Back to Login
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      {/* <ToastContainer /> */}
    </Container>
  );
}
